import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Loader, Menu } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCircleDot,
  IconCopy,
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconForms,
  IconLock,
  IconPlus,
  IconTextSize,
  IconTrash,
} from '@tabler/icons-react';
import {
  useCompetencies,
  useCreateQuestionTemplate,
  useDeleteQuestionTemplate,
  useDuplicateQuestionTemplate,
  useQuestionTemplate,
  useUpdateQuestionTemplate,
} from '../../../hooks/useApi';
import { useAnalytics, AnalyticsEvents } from '../../../analytics';
import { Breadcrumbs } from '../../../components/ui/Breadcrumbs';
import { showToast } from '../../../utils/toast';
import { getErrorMessage } from '../../../utils/api';
import { Competency, QuestionTemplate, Question360Scale } from '../../../types';

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────
// Цвета для бейджей-компетенций. Применяются циклически по индексу. Когда у
// Competency появится свой `color`-поле, можно будет переключиться на него.
const COMPETENCY_COLOR_POOL = [
  '#12B886',
  '#228BE6',
  '#BE4BDB',
  '#F08C00',
  '#0EA5E9',
  '#D946EF',
  '#FF6B6B',
  '#7048E8',
];

type ScalePresetId = '5-frequency' | '5-agree' | '5-strength' | '7-agree';

interface ScalePreset {
  id: ScalePresetId;
  labelKey: string; // строка-кей для t()
  fallback: string;
  min: number;
  max: number;
  leftKey: string;
  leftFallback: string;
  rightKey: string;
  rightFallback: string;
}

const SCALE_PRESETS: ScalePreset[] = [
  {
    id: '5-frequency',
    labelKey: 'review360.templateEditor.scale5freq',
    fallback: '1–5 · Частота',
    min: 1,
    max: 5,
    leftKey: 'review360.templateEditor.scaleNever',
    leftFallback: 'Никогда',
    rightKey: 'review360.templateEditor.scaleAlways',
    rightFallback: 'Всегда',
  },
  {
    id: '5-agree',
    labelKey: 'review360.templateEditor.scale5agree',
    fallback: '1–5 · Согласие',
    min: 1,
    max: 5,
    leftKey: 'review360.templateEditor.scaleDisagree',
    leftFallback: 'Совсем не согласен',
    rightKey: 'review360.templateEditor.scaleAgree',
    rightFallback: 'Полностью согласен',
  },
  {
    id: '5-strength',
    labelKey: 'review360.templateEditor.scale5strength',
    fallback: '1–5 · Степень',
    min: 1,
    max: 5,
    leftKey: 'review360.templateEditor.scaleWeak',
    leftFallback: 'Очень слабо',
    rightKey: 'review360.templateEditor.scaleStrong',
    rightFallback: 'Очень сильно',
  },
  {
    id: '7-agree',
    labelKey: 'review360.templateEditor.scale7agree',
    fallback: '1–7 · Согласие',
    min: 1,
    max: 7,
    leftKey: 'review360.templateEditor.scaleDisagree',
    leftFallback: 'Совсем не согласен',
    rightKey: 'review360.templateEditor.scaleAgree',
    rightFallback: 'Полностью согласен',
  },
];

const SCALE_BY_MAX: Record<number, ScalePresetId> = {
  5: '5-strength',
  7: '7-agree',
};

const NEW_PREFIX = 'new-';
const genId = (p: string) => `${NEW_PREFIX}${p}-${Math.random().toString(36).slice(2, 8)}`;

// ────────────────────────────────────────────────────────────────────────────
// UI state model
// ────────────────────────────────────────────────────────────────────────────
interface UiQuestion {
  _id: string;
  text: string;
  type: 'rating' | 'open';
  scaleId: ScalePresetId;
  required: boolean;
  /** UI-only пока — не хранится в БД. См. TODO в конце файла. */
  anonymous: boolean;
  expanded: boolean;
}

interface UiSection {
  _id: string;
  title: string;
  competencyId: string | null;
  expanded: boolean;
  questions: UiQuestion[];
}

interface UiState {
  name: string;
  description: string;
  sections: UiSection[];
  /** UI-only пока — не хранится в БД. См. TODO. */
  published: boolean;
}

const emptyState = (): UiState => ({
  name: '',
  description: '',
  sections: [],
  published: false,
});

// Резолвим scale в UI-пресет: если бэк прислал preset → используем его;
// иначе пытаемся подобрать по min/max + лейблам; в крайнем случае — по
// старому scaleMax (backward-compat для шаблонов, созданных до миграции схемы).
const resolveScaleId = (q: { scale?: Question360Scale; scaleMax?: number }): ScalePresetId => {
  if (q.scale?.preset && SCALE_PRESETS.some((p) => p.id === q.scale!.preset)) {
    return q.scale.preset as ScalePresetId;
  }
  if (q.scale) {
    // Эвристика: ищем совпадение min/max + leftLabel.
    const exact = SCALE_PRESETS.find(
      (p) =>
        p.min === q.scale!.min && p.max === q.scale!.max && p.leftFallback === q.scale!.leftLabel,
    );
    if (exact) return exact.id;
    if (q.scale.max === 7) return '7-agree';
    return '5-strength';
  }
  return SCALE_BY_MAX[q.scaleMax ?? 5] ?? '5-strength';
};

const stateFromTemplate = (tpl: QuestionTemplate): UiState => ({
  name: tpl.name,
  description: tpl.description ?? '',
  sections: (tpl.sections ?? []).map((s) => ({
    _id: s._id,
    title: s.title,
    competencyId: s.competencyId ?? null,
    expanded: false,
    questions: (s.questions ?? []).map((q) => ({
      _id: q._id,
      text: q.text,
      type: q.type === 'open_ended' ? 'open' : 'rating',
      scaleId: resolveScaleId(q),
      required: q.required,
      anonymous: q.anonymous ?? false,
      expanded: false,
    })),
  })),
  published: tpl.isPublished ?? false,
});

// ────────────────────────────────────────────────────────────────────────────
// Presets для empty state (UI-only)
// ────────────────────────────────────────────────────────────────────────────
interface Preset {
  id: string;
  name: string;
  description: string;
  sections: number;
  questions: number;
  from: string;
  to: string;
  build: () => UiSection[];
}

const buildEmSections = (): UiSection[] => [
  {
    _id: genId('s'),
    title: 'Tech leadership',
    competencyId: null,
    expanded: true,
    questions: [
      {
        _id: genId('q'),
        text: 'Насколько коллега двигает технические решения команды?',
        type: 'rating',
        scaleId: '5-strength',
        required: true,
        anonymous: true,
        expanded: false,
      },
      {
        _id: genId('q'),
        text: 'Опиши конкретный пример сильного технического решения за последние полгода',
        type: 'open',
        scaleId: '5-strength',
        required: false,
        anonymous: true,
        expanded: false,
      },
    ],
  },
  {
    _id: genId('s'),
    title: 'Менторство',
    competencyId: null,
    expanded: true,
    questions: [
      {
        _id: genId('q'),
        text: 'Помогает ли расти джунам и мидлам?',
        type: 'rating',
        scaleId: '5-frequency',
        required: true,
        anonymous: true,
        expanded: false,
      },
      {
        _id: genId('q'),
        text: 'Эффективно ли даёт обратную связь?',
        type: 'rating',
        scaleId: '5-strength',
        required: true,
        anonymous: true,
        expanded: false,
      },
    ],
  },
  {
    _id: genId('s'),
    title: 'Коммуникация',
    competencyId: null,
    expanded: false,
    questions: [
      {
        _id: genId('q'),
        text: 'Чётко ли формулирует мысли?',
        type: 'rating',
        scaleId: '5-strength',
        required: true,
        anonymous: true,
        expanded: false,
      },
    ],
  },
];

const PRESETS: Preset[] = [
  {
    id: 'em',
    name: 'Engineering Manager',
    description: 'Готовый шаблон для ревью менеджеров. 3 секции, 5 вопросов.',
    sections: 3,
    questions: 5,
    from: '#0A6B5A',
    to: '#12B886',
    build: buildEmSections,
  },
  {
    id: 'sic',
    name: 'Senior IC',
    description: 'Для индивидуальных контрибьюторов. Фокус на технике и кросс-функциональности.',
    sections: 2,
    questions: 4,
    from: '#0B4E72',
    to: '#228BE6',
    build: () => [
      {
        _id: genId('s'),
        title: 'System Design',
        competencyId: null,
        expanded: true,
        questions: [
          {
            _id: genId('q'),
            text: 'Принимает ли решения уровня staff: компромиссы, масштабирование, надёжность?',
            type: 'rating',
            scaleId: '5-strength',
            required: true,
            anonymous: true,
            expanded: false,
          },
          {
            _id: genId('q'),
            text: 'Опиши пример сильного дизайн-решения',
            type: 'open',
            scaleId: '5-strength',
            required: false,
            anonymous: true,
            expanded: false,
          },
        ],
      },
      {
        _id: genId('s'),
        title: 'Импакт',
        competencyId: null,
        expanded: false,
        questions: [
          {
            _id: genId('q'),
            text: 'Виден ли его вклад в бизнес-метрики команды?',
            type: 'rating',
            scaleId: '5-agree',
            required: true,
            anonymous: true,
            expanded: false,
          },
          {
            _id: genId('q'),
            text: 'Какой проект коллеги принёс наибольшую пользу?',
            type: 'open',
            scaleId: '5-strength',
            required: false,
            anonymous: true,
            expanded: false,
          },
        ],
      },
    ],
  },
  {
    id: 'des',
    name: 'Designer · 360°',
    description: 'Продуктовый дизайнер: визуальный, UX и кросс-функциональный взгляд.',
    sections: 2,
    questions: 4,
    from: '#3D2461',
    to: '#7048E8',
    build: () => [
      {
        _id: genId('s'),
        title: 'UX-мышление',
        competencyId: null,
        expanded: true,
        questions: [
          {
            _id: genId('q'),
            text: 'Чётко ли отделяет проблему от решения?',
            type: 'rating',
            scaleId: '5-strength',
            required: true,
            anonymous: true,
            expanded: false,
          },
          {
            _id: genId('q'),
            text: 'Учитывает ли разные сценарии и edge-cases?',
            type: 'rating',
            scaleId: '5-frequency',
            required: true,
            anonymous: true,
            expanded: false,
          },
        ],
      },
      {
        _id: genId('s'),
        title: 'Кросс-функциональность',
        competencyId: null,
        expanded: false,
        questions: [
          {
            _id: genId('q'),
            text: 'Хорошо ли договаривается с инжем и продактами?',
            type: 'rating',
            scaleId: '5-agree',
            required: true,
            anonymous: true,
            expanded: false,
          },
          {
            _id: genId('q'),
            text: 'Опиши пример успешного кросс-функционального решения',
            type: 'open',
            scaleId: '5-strength',
            required: false,
            anonymous: true,
            expanded: false,
          },
        ],
      },
    ],
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────
export const Review360TemplateEditor: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { track } = useAnalytics();

  const isCreate = id === 'new';
  const { data: tpl, isLoading, error } = useQuestionTemplate(isCreate ? '' : id || '');
  const { data: competencies = [] } = useCompetencies();

  const createMut = useCreateQuestionTemplate();
  const updateMut = useUpdateQuestionTemplate();
  const duplicateMut = useDuplicateQuestionTemplate();
  const deleteMut = useDeleteQuestionTemplate();
  const isSaving = createMut.isPending || updateMut.isPending;

  const [state, setState] = useState<UiState>(emptyState);
  const [saved, setSaved] = useState<UiState>(emptyState);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'phone'>('desktop');

  useEffect(() => {
    if (tpl && !isCreate) {
      const next = stateFromTemplate(tpl);
      setState(next);
      setSaved(next);
    }
  }, [tpl, isCreate]);

  const set = <K extends keyof UiState>(k: K, v: UiState[K]) => setState((s) => ({ ...s, [k]: v }));

  // Empty-state «С чего начнём?» — только в create-режиме при чистом state
  const showPresets =
    isCreate && state.sections.length === 0 && !state.name.trim() && !state.description.trim();

  const competencyColor = useMemo(() => {
    const map = new Map<string, string>();
    (competencies as Competency[]).forEach((c, i) => {
      map.set(c._id, COMPETENCY_COLOR_POOL[i % COMPETENCY_COLOR_POOL.length]);
    });
    return map;
  }, [competencies]);

  const totalQuestions = state.sections.reduce((a, s) => a + s.questions.length, 0);
  const requiredCount = state.sections.reduce(
    (a, s) => a + s.questions.filter((q) => q.required).length,
    0,
  );
  const anonymousCount = state.sections.reduce(
    (a, s) => a + s.questions.filter((q) => q.anonymous).length,
    0,
  );
  const estTime = Math.max(2, Math.round(totalQuestions * 0.7));

  const hasChanges = isCreate
    ? state.sections.length > 0 || !!state.name.trim()
    : JSON.stringify(state) !== JSON.stringify(saved);
  const canSave =
    state.name.trim().length > 0 &&
    state.sections.length > 0 &&
    state.sections.every((s) => s.questions.length > 0);

  // ── Section ops ──
  const addSection = () =>
    set('sections', [
      ...state.sections,
      {
        _id: genId('s'),
        title: '',
        competencyId: null,
        expanded: true,
        questions: [
          {
            _id: genId('q'),
            text: '',
            type: 'rating',
            scaleId: '5-strength',
            required: true,
            anonymous: true,
            expanded: true,
          },
        ],
      },
    ]);
  const updateSection = (sid: string, patch: Partial<UiSection>) =>
    set(
      'sections',
      state.sections.map((s) => (s._id === sid ? { ...s, ...patch } : s)),
    );
  const removeSection = (sid: string) =>
    set(
      'sections',
      state.sections.filter((s) => s._id !== sid),
    );
  const moveSection = (sid: string, dir: -1 | 1) => {
    const idx = state.sections.findIndex((s) => s._id === sid);
    const target = idx + dir;
    if (target < 0 || target >= state.sections.length) return;
    const next = [...state.sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    set('sections', next);
  };
  const setAllExpanded = (val: boolean) =>
    set(
      'sections',
      state.sections.map((s) => ({ ...s, expanded: val })),
    );

  // ── Question ops ──
  const addQuestion = (sid: string, type: 'rating' | 'open') =>
    updateSection(sid, {
      questions: [
        ...(state.sections.find((s) => s._id === sid)?.questions || []),
        {
          _id: genId('q'),
          text: '',
          type,
          scaleId: '5-strength',
          required: true,
          anonymous: true,
          expanded: true,
        },
      ],
    });
  const updateQuestion = (sid: string, qid: string, patch: Partial<UiQuestion>) => {
    const section = state.sections.find((s) => s._id === sid);
    if (!section) return;
    updateSection(sid, {
      questions: section.questions.map((q) => (q._id === qid ? { ...q, ...patch } : q)),
    });
  };
  const removeQuestion = (sid: string, qid: string) => {
    const section = state.sections.find((s) => s._id === sid);
    if (!section) return;
    updateSection(sid, { questions: section.questions.filter((q) => q._id !== qid) });
  };

  // ── Preset / blank start ──
  const startBlank = () => addSection();
  const startFromPreset = (preset: Preset) => {
    const next: UiState = {
      ...state,
      name: preset.name,
      description: preset.description,
      sections: preset.build(),
    };
    setState(next);
  };

  // ── Save ──
  const buildPayload = (overrides?: Partial<QuestionTemplate>) => ({
    name: state.name.trim(),
    description: state.description.trim() || undefined,
    isPublished: state.published,
    sections: state.sections.map((s) => ({
      // Сохраняем существующий _id, чтобы апдейт не дёргал индексы.
      ...(s._id.startsWith(NEW_PREFIX) ? {} : { _id: s._id }),
      title: s.title,
      competencyId: s.competencyId ?? undefined,
      questions: s.questions.map((q) => {
        const scale = SCALE_PRESETS.find((p) => p.id === q.scaleId) ?? SCALE_PRESETS[2];
        const isRating = q.type === 'rating';
        return {
          ...(q._id.startsWith(NEW_PREFIX) ? {} : { _id: q._id }),
          text: q.text,
          type: isRating ? 'rating' : 'open_ended',
          // Новое поле — полная семантика шкалы. Старый scaleMax оставляем
          // тоже, чтобы старые консьюмеры (если есть) не сломались.
          scale: isRating
            ? {
                min: scale.min,
                max: scale.max,
                leftLabel: scale.leftFallback,
                rightLabel: scale.rightFallback,
                preset: scale.id,
              }
            : undefined,
          scaleMax: isRating ? scale.max : undefined,
          required: q.required,
          anonymous: q.anonymous,
        };
      }),
    })),
    ...overrides,
  });

  // Быстрая операция «опубликовать / снять с публикации» из action-меню.
  // Шлёт сразу полный payload вместе с новым `isPublished`, чтобы не плодить
  // отдельные эндпоинты. canSave требует валидного состояния.
  const handleTogglePublish = (next: boolean) => {
    if (isCreate || !id) return;
    if (next && !canSave) {
      showToast({
        title: t('review360.templateEditor.cantPublishTitle'),
        message: t('review360.templateEditor.cantPublishMsg'),
        type: 'error',
      });
      return;
    }
    updateMut.mutate(
      {
        id,
        data: buildPayload({
          isPublished: next,
        } as Partial<QuestionTemplate>) as Partial<QuestionTemplate>,
      },
      {
        onSuccess: () => {
          set('published', next);
          setSaved({ ...state, published: next });
          showToast({
            title: next
              ? t('review360.templateEditor.publishedToast')
              : t('review360.templateEditor.draftedToast'),
            message: '',
          });
        },
        onError: (err) =>
          showToast({
            title: t('common.error'),
            message: getErrorMessage(err, t('common.error')),
            type: 'error',
          }),
      },
    );
  };

  const handleDuplicate = () => {
    if (isCreate || !id) return;
    duplicateMut.mutate(id, {
      onSuccess: (copy) => {
        showToast({ title: t('review360.templateEditor.duplicatedToast'), message: '' });
        navigate(`/review-360/templates/${copy._id}`);
      },
      onError: (err) =>
        showToast({
          title: t('common.error'),
          message: getErrorMessage(err, t('common.error')),
          type: 'error',
        }),
    });
  };

  const handleDelete = () => {
    if (isCreate || !id) return;
    modals.openConfirmModal({
      title: t('review360.templateEditor.deleteConfirmTitle'),
      children: (
        <span>
          {t('review360.templateEditor.deleteConfirmText', {
            name: state.name || t('review360.templateEditor.untitled'),
          })}
        </span>
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteMut.mutate(id, {
          onSuccess: () => {
            showToast({ title: t('review360.templateEditor.deletedToast'), message: '' });
            navigate('/review-360/templates');
          },
          onError: (err) =>
            showToast({
              title: t('common.error'),
              message: getErrorMessage(err, t('common.error')),
              type: 'error',
            }),
        }),
    });
  };

  const handleSave = () => {
    if (!canSave) return;
    const payload = buildPayload();

    if (isCreate) {
      createMut.mutate(payload as unknown as Partial<QuestionTemplate>, {
        onSuccess: () => {
          track(AnalyticsEvents.REVIEW_360_TEMPLATE_CREATED, { name: payload.name });
          showToast({
            title: t('review360.templateSaved'),
            message: t('review360.templateSavedMsg'),
          });
          navigate('/review-360/templates');
        },
        onError: (err) =>
          showToast({
            title: t('common.error'),
            message: getErrorMessage(err, t('common.error')),
            type: 'error',
          }),
      });
    } else {
      updateMut.mutate(
        { id: id!, data: payload as unknown as Partial<QuestionTemplate> },
        {
          onSuccess: () => {
            setSaved(state);
            showToast({
              title: t('review360.templateSaved'),
              message: t('review360.templateSavedMsg'),
            });
          },
          onError: (err) =>
            showToast({
              title: t('common.error'),
              message: getErrorMessage(err, t('common.error')),
              type: 'error',
            }),
        },
      );
    }
  };

  // ── Render ──
  if (!isCreate && error) {
    return (
      <div className="tpl-page" style={{ padding: 32 }}>
        <Alert icon={<IconAlertCircle size={16} />} title={t('common.error')} color="red">
          {t('messages.errorLoading', { item: t('review360.templates') })}
        </Alert>
      </div>
    );
  }
  if (!isCreate && isLoading) {
    return (
      <div className="tpl-page" style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
        <Loader size="sm" />
      </div>
    );
  }

  return (
    <div className="tpl-page">
      <style>{templateEditorCss}</style>

      <div className="tpl-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
          <Breadcrumbs
            items={[
              { label: 'Review 360°', to: '/review-360' },
              {
                label: t('review360.templateEditor.breadcrumbTemplates'),
                to: '/review-360/templates',
              },
              {
                label: isCreate
                  ? t('review360.templateEditor.newTemplate')
                  : state.name || t('review360.templateEditor.untitled'),
              },
            ]}
          />
          <span
            className={`tpl-pill ${state.published ? 'tpl-pill-accent' : 'tpl-pill-warn'}`}
            style={{ marginLeft: 6 }}
          >
            {state.published
              ? t('review360.templateEditor.pillPublished')
              : t('review360.templateEditor.pillDraft')}
          </span>
        </div>
        {!isCreate && (
          <Menu shadow="md" width={220} position="bottom-end">
            <Menu.Target>
              <Button variant="default" size="xs" rightSection={<IconDotsVertical size={14} />}>
                {t('review360.templateEditor.actions')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {state.published ? (
                <Menu.Item
                  leftSection={<IconEyeOff size={14} />}
                  onClick={() => handleTogglePublish(false)}
                >
                  {t('review360.templateEditor.actionUnpublish')}
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<IconEye size={14} />}
                  onClick={() => handleTogglePublish(true)}
                  disabled={!canSave}
                >
                  {t('review360.templateEditor.actionPublish')}
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconCopy size={14} />} onClick={handleDuplicate}>
                {t('review360.templateEditor.actionDuplicate')}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={handleDelete}>
                {t('common.delete')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </div>

      {showPresets ? (
        <div className="tpl-preset-wrap">
          <div className="tpl-eyebrow" style={{ marginBottom: 10 }}>
            {t('review360.templateEditor.presetEyebrow')}
          </div>
          <h1 className="tpl-hero-title">{t('review360.templateEditor.presetTitle')}</h1>
          <p className="tpl-hero-subtitle">{t('review360.templateEditor.presetSubtitle')}</p>
          <div className="tpl-preset-grid">
            <button type="button" onClick={startBlank} className="tpl-preset-blank">
              <div className="tpl-preset-blank-icon">
                <IconPlus size={22} />
              </div>
              <div className="tpl-preset-title">
                {t('review360.templateEditor.presetBlankTitle')}
              </div>
              <div className="tpl-preset-desc">{t('review360.templateEditor.presetBlankDesc')}</div>
            </button>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => startFromPreset(p)}
                className="tpl-preset-card"
              >
                <div
                  className="tpl-preset-cover"
                  style={{ background: `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)` }}
                />
                <div className="tpl-preset-body">
                  <div className="tpl-preset-title">{p.name}</div>
                  <div className="tpl-preset-desc">{p.description}</div>
                  <div className="tpl-preset-meta">
                    <span className="num">
                      {t('review360.templateEditor.presetSections', { count: p.sections })}
                    </span>
                    <span className="tpl-dot-mini" />
                    <span className="num">
                      {t('review360.templateEditor.presetQuestions', { count: p.questions })}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="tpl-content">
          {/* TitleHero */}
          <div>
            <div className="tpl-eyebrow" style={{ marginBottom: 10 }}>
              {t('review360.templateEditor.titleEyebrow')}
            </div>
            <textarea
              rows={1}
              value={state.name}
              onChange={(e) => set('name', e.currentTarget.value)}
              placeholder={t('review360.templateEditor.titlePlaceholder')}
              className="tpl-title-input"
            />
            <textarea
              rows={2}
              value={state.description}
              onChange={(e) => set('description', e.currentTarget.value)}
              placeholder={t('review360.templateEditor.descriptionPlaceholder')}
              className="tpl-desc-input"
            />
          </div>

          <div className="tpl-grid">
            <div className="tpl-col-main">
              <SectionsEditor
                state={state}
                competencies={competencies as Competency[]}
                competencyColor={competencyColor}
                onAddSection={addSection}
                onUpdateSection={updateSection}
                onRemoveSection={removeSection}
                onMoveSection={moveSection}
                onSetAllExpanded={setAllExpanded}
                onAddQuestion={addQuestion}
                onUpdateQuestion={updateQuestion}
                onRemoveQuestion={removeQuestion}
              />
              {state.sections.length > 0 && (
                <div className="tpl-summary-row">
                  <SummaryCard
                    sectionCount={state.sections.length}
                    totalQuestions={totalQuestions}
                    requiredCount={requiredCount}
                    anonymousCount={anonymousCount}
                    estTime={estTime}
                  />
                  <CoverageCard
                    state={state}
                    competencies={competencies as Competency[]}
                    competencyColor={competencyColor}
                  />
                </div>
              )}
            </div>

            <LivePreview
              state={state}
              competencies={competencies as Competency[]}
              competencyColor={competencyColor}
              device={previewDevice}
              onChangeDevice={setPreviewDevice}
              totalQuestions={totalQuestions}
              estTime={estTime}
            />
          </div>
        </div>
      )}

      {hasChanges && !showPresets && (
        <div className="tpl-savebar">
          <span className="tpl-dot tpl-dot-warn" />
          <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
            {isCreate
              ? t('review360.templateEditor.savebarCreating')
              : t('review360.templateEditor.savebarUnsaved')}
          </span>
          {!isCreate && (
            <button type="button" className="tpl-discard" onClick={() => setState(saved)}>
              {t('review360.templateEditor.savebarDiscard')}
            </button>
          )}
          <button
            type="button"
            className="tpl-save"
            onClick={handleSave}
            disabled={!canSave || isSaving}
            title={!canSave ? t('review360.templateEditor.savebarCantSave') : undefined}
          >
            {isSaving ? (
              t('review360.templateEditor.savebarSaving')
            ) : isCreate ? (
              t('review360.templateEditor.savebarCreate')
            ) : (
              <>
                <IconCheck size={14} /> {t('review360.templateEditor.savebarSave')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────────
const SectionsEditor: React.FC<{
  state: UiState;
  competencies: Competency[];
  competencyColor: Map<string, string>;
  onAddSection: () => void;
  onUpdateSection: (sid: string, patch: Partial<UiSection>) => void;
  onRemoveSection: (sid: string) => void;
  onMoveSection: (sid: string, dir: -1 | 1) => void;
  onSetAllExpanded: (v: boolean) => void;
  onAddQuestion: (sid: string, type: 'rating' | 'open') => void;
  onUpdateQuestion: (sid: string, qid: string, patch: Partial<UiQuestion>) => void;
  onRemoveQuestion: (sid: string, qid: string) => void;
}> = ({
  state,
  competencies,
  competencyColor,
  onAddSection,
  onUpdateSection,
  onRemoveSection,
  onMoveSection,
  onSetAllExpanded,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
}) => {
  const { t } = useTranslation();
  const count = state.sections.length;
  return (
    <div className="tpl-sections">
      <div className="tpl-sections-head">
        <div>
          <div className="tpl-eyebrow">{t('review360.templateEditor.structureEyebrow')}</div>
          <h2 className="tpl-section-title">
            {count === 0
              ? t('review360.templateEditor.empty')
              : t('review360.templateEditor.sections', { count })}
          </h2>
        </div>
        {count > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="subtle" size="xs" onClick={() => onSetAllExpanded(true)}>
              {t('review360.templateEditor.expandAll')}
            </Button>
            <Button variant="subtle" size="xs" onClick={() => onSetAllExpanded(false)}>
              {t('review360.templateEditor.collapseAll')}
            </Button>
          </div>
        )}
      </div>

      {count === 0 ? (
        <div className="tpl-empty">
          <div className="tpl-empty-icon">
            <IconForms size={24} />
          </div>
          <h3 className="tpl-empty-title">{t('review360.templateEditor.emptyStateTitle')}</h3>
          <p className="tpl-empty-desc">{t('review360.templateEditor.emptyStateDesc')}</p>
          <Button leftSection={<IconPlus size={14} />} onClick={onAddSection}>
            {t('review360.templateEditor.createSection')}
          </Button>
        </div>
      ) : (
        <div className="tpl-section-list">
          {state.sections.map((s, i) => (
            <SectionCard
              key={s._id}
              section={s}
              idx={i + 1}
              competencies={competencies}
              competencyColor={competencyColor}
              isFirst={i === 0}
              isLast={i === count - 1}
              onUpdate={(patch) => onUpdateSection(s._id, patch)}
              onRemove={() => onRemoveSection(s._id)}
              onMove={(dir) => onMoveSection(s._id, dir)}
              onAddQuestion={(type) => onAddQuestion(s._id, type)}
              onUpdateQuestion={(qid, patch) => onUpdateQuestion(s._id, qid, patch)}
              onRemoveQuestion={(qid) => onRemoveQuestion(s._id, qid)}
            />
          ))}
        </div>
      )}

      {count > 0 && (
        <button type="button" onClick={onAddSection} className="tpl-add-section">
          <IconPlus size={14} /> {t('review360.templateEditor.addSection')}
        </button>
      )}
    </div>
  );
};

const SectionCard: React.FC<{
  section: UiSection;
  idx: number;
  competencies: Competency[];
  competencyColor: Map<string, string>;
  isFirst: boolean;
  isLast: boolean;
  onUpdate: (patch: Partial<UiSection>) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  onAddQuestion: (type: 'rating' | 'open') => void;
  onUpdateQuestion: (qid: string, patch: Partial<UiQuestion>) => void;
  onRemoveQuestion: (qid: string) => void;
}> = ({
  section,
  idx,
  competencies,
  competencyColor,
  isFirst,
  isLast,
  onUpdate,
  onRemove,
  onMove,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
}) => {
  const { t } = useTranslation();
  return (
    <div className="tpl-section">
      <div className="tpl-section-head">
        <div className="tpl-sort">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => onMove(-1)}
            className="tpl-sort-btn"
          >
            <IconChevronUp size={14} />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => onMove(1)}
            className="tpl-sort-btn"
          >
            <IconChevronDown size={14} />
          </button>
        </div>
        <div className="tpl-num">{idx}</div>
        <input
          type="text"
          value={section.title}
          onChange={(e) => onUpdate({ title: e.currentTarget.value })}
          placeholder={t('review360.templateEditor.sectionTitlePlaceholder')}
          className="tpl-section-title-input"
        />
        <CompetencyPicker
          section={section}
          competencies={competencies}
          competencyColor={competencyColor}
          onUpdate={onUpdate}
        />
        <span className="tpl-pill tpl-pill-ghost">
          {t('review360.templateEditor.questionsShort', { count: section.questions.length })}
        </span>
        <button
          type="button"
          className="tpl-icon-btn"
          onClick={() => onUpdate({ expanded: !section.expanded })}
          aria-label={
            section.expanded
              ? t('review360.templateEditor.collapse')
              : t('review360.templateEditor.expand')
          }
        >
          <IconChevronDown
            size={14}
            style={{
              transform: section.expanded ? 'rotate(180deg)' : undefined,
              transition: 'transform 120ms',
            }}
          />
        </button>
        <button
          type="button"
          className="tpl-icon-btn tpl-icon-btn-danger"
          onClick={onRemove}
          aria-label={t('review360.templateEditor.removeSection')}
        >
          <IconTrash size={14} />
        </button>
      </div>

      {section.expanded && (
        <div className="tpl-section-body">
          {section.questions.map((q, qi) => (
            <QuestionCard
              key={q._id}
              q={q}
              qi={qi + 1}
              onUpdate={(patch) => onUpdateQuestion(q._id, patch)}
              onRemove={() => onRemoveQuestion(q._id)}
            />
          ))}
          <div className="tpl-add-question-row">
            <button
              type="button"
              onClick={() => onAddQuestion('rating')}
              className="tpl-add-question"
            >
              <IconPlus size={14} /> {t('review360.templateEditor.addRatingQuestion')}
            </button>
            <button
              type="button"
              onClick={() => onAddQuestion('open')}
              className="tpl-add-question"
            >
              <IconPlus size={14} /> {t('review360.templateEditor.addOpenQuestion')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CompetencyPicker: React.FC<{
  section: UiSection;
  competencies: Competency[];
  competencyColor: Map<string, string>;
  onUpdate: (patch: Partial<UiSection>) => void;
}> = ({ section, competencies, competencyColor, onUpdate }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const current = competencies.find((c) => c._id === section.competencyId);
  const color = current ? competencyColor.get(current._id) : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="tpl-comp-trigger"
        style={
          current && color
            ? {
                background: `${color}1F`,
                color,
                borderColor: 'transparent',
              }
            : undefined
        }
      >
        {current && color ? (
          <span className="tpl-dot" style={{ background: color }} />
        ) : (
          <IconPlus size={10} />
        )}
        {current ? current.title : t('review360.templateEditor.competencyPickerNone')}
      </button>
      {open && (
        <div className="tpl-comp-dropdown">
          <button
            type="button"
            className={`tpl-comp-item${section.competencyId === null ? ' tpl-comp-item-active' : ''}`}
            onClick={() => {
              onUpdate({ competencyId: null });
              setOpen(false);
            }}
          >
            <span className="tpl-dot tpl-dot-soft" />
            {t('review360.templateEditor.competencyNoLink')}
            {section.competencyId === null && (
              <IconCheck size={14} style={{ marginLeft: 'auto' }} />
            )}
          </button>
          {competencies.length === 0 && (
            <div className="tpl-comp-empty">
              {t('review360.templateEditor.competencyCatalogEmpty')}
            </div>
          )}
          {competencies.map((c) => {
            const isActive = c._id === section.competencyId;
            const col = competencyColor.get(c._id);
            return (
              <button
                key={c._id}
                type="button"
                className={`tpl-comp-item${isActive ? ' tpl-comp-item-active' : ''}`}
                onClick={() => {
                  onUpdate({ competencyId: c._id });
                  setOpen(false);
                }}
              >
                <span className="tpl-dot" style={{ background: col }} />
                {c.title}
                {isActive && <IconCheck size={14} style={{ marginLeft: 'auto' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const QuestionCard: React.FC<{
  q: UiQuestion;
  qi: number;
  onUpdate: (patch: Partial<UiQuestion>) => void;
  onRemove: () => void;
}> = ({ q, qi, onUpdate, onRemove }) => {
  const { t } = useTranslation();
  const isRating = q.type === 'rating';
  return (
    <div className={`tpl-question${q.expanded ? ' tpl-question-open' : ''}`}>
      <div className="tpl-question-head">
        <div className="tpl-q-num">{qi}</div>
        <input
          type="text"
          value={q.text}
          onChange={(e) => onUpdate({ text: e.currentTarget.value })}
          placeholder={
            isRating
              ? t('review360.templateEditor.questionTextRatingPlaceholder')
              : t('review360.templateEditor.questionTextOpenPlaceholder')
          }
          className="tpl-q-input"
        />
        <div className="tpl-q-typetabs">
          {(
            [
              {
                v: 'rating',
                label: t('review360.templateEditor.questionTypeRating'),
                icon: <IconCircleDot size={12} />,
              },
              {
                v: 'open',
                label: t('review360.templateEditor.questionTypeOpen'),
                icon: <IconTextSize size={12} />,
              },
            ] as Array<{ v: UiQuestion['type']; label: string; icon: React.ReactNode }>
          ).map((tab) => {
            const active = tab.v === q.type;
            return (
              <button
                key={tab.v}
                type="button"
                onClick={() => onUpdate({ type: tab.v })}
                className={`tpl-q-typetab${active ? ' tpl-q-typetab-active' : ''}`}
              >
                {tab.icon} {tab.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="tpl-icon-btn"
          onClick={() => onUpdate({ expanded: !q.expanded })}
          aria-label={t('review360.templateEditor.collapse')}
        >
          <IconChevronDown
            size={14}
            style={{
              transform: q.expanded ? 'rotate(180deg)' : undefined,
              transition: 'transform 120ms',
            }}
          />
        </button>
        <button
          type="button"
          className="tpl-icon-btn tpl-icon-btn-danger"
          onClick={onRemove}
          aria-label={t('review360.templateEditor.removeQuestion')}
        >
          <IconTrash size={14} />
        </button>
      </div>

      {q.expanded && (
        <div className="tpl-question-body">
          {isRating && (
            <div>
              <div className="tpl-eyebrow" style={{ marginBottom: 8 }}>
                {t('review360.templateEditor.scaleLabel')}
              </div>
              <div className="tpl-scales">
                {SCALE_PRESETS.map((s) => {
                  const active = s.id === q.scaleId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onUpdate({ scaleId: s.id })}
                      className={`tpl-scale-chip${active ? ' tpl-scale-chip-active' : ''}`}
                    >
                      {active && <IconCheck size={11} />}
                      {t(s.labelKey, s.fallback)}
                    </button>
                  );
                })}
              </div>
              <div className="tpl-scale-hint">
                {t('review360.templateEditor.scaleEndLabels')}{' '}
                <strong>{t(scaleOf(q.scaleId).leftKey, scaleOf(q.scaleId).leftFallback)}</strong> ←→{' '}
                <strong>{t(scaleOf(q.scaleId).rightKey, scaleOf(q.scaleId).rightFallback)}</strong>
              </div>
            </div>
          )}
          <div className="tpl-toggles">
            <Toggle
              label={t('review360.templateEditor.toggleRequired')}
              checked={q.required}
              onChange={(v) => onUpdate({ required: v })}
            />
            <Toggle
              label={t('review360.templateEditor.toggleAnonymous')}
              checked={q.anonymous}
              onChange={(v) => onUpdate({ anonymous: v })}
              hint={t('review360.templateEditor.toggleAnonymousHint')}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}> = ({ label, checked, onChange, hint }) => (
  <label className="tpl-toggle-row">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className={`tpl-toggle${checked ? ' tpl-toggle-on' : ''}`}
    >
      <span className="tpl-toggle-knob" />
    </button>
    <span style={{ fontWeight: 500 }}>{label}</span>
    {hint && <span style={{ fontSize: 11, color: 'var(--tpl-muted)' }}>· {hint}</span>}
  </label>
);

const SummaryCard: React.FC<{
  sectionCount: number;
  totalQuestions: number;
  requiredCount: number;
  anonymousCount: number;
  estTime: number;
}> = ({ sectionCount, totalQuestions, requiredCount, anonymousCount, estTime }) => {
  const { t } = useTranslation();
  return (
    <div className="tpl-summary">
      <div className="tpl-eyebrow">{t('review360.templateEditor.summaryEyebrow')}</div>
      <div className="tpl-summary-grid">
        <SummaryStat label={t('review360.templateEditor.summarySections')} value={sectionCount} />
        <SummaryStat
          label={t('review360.templateEditor.summaryTotalQuestions')}
          value={totalQuestions}
        />
        <SummaryStat label={t('review360.templateEditor.summaryRequired')} value={requiredCount} />
        <SummaryStat
          label={t('review360.templateEditor.summaryAnonymous')}
          value={anonymousCount}
        />
      </div>
      <div className="tpl-summary-time">
        <span>{t('review360.templateEditor.summaryAvgTime')}</span>
        <span className="num">
          {t('review360.templateEditor.summaryAvgTimeValue', { n: estTime })}
        </span>
      </div>
    </div>
  );
};

const SummaryStat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div>
    <div className="tpl-eyebrow">{label}</div>
    <div className="num tpl-stat-value">{value}</div>
  </div>
);

const CoverageCard: React.FC<{
  state: UiState;
  competencies: Competency[];
  competencyColor: Map<string, string>;
}> = ({ state, competencies, competencyColor }) => {
  const { t } = useTranslation();
  const counts: Record<string, number> = {};
  for (const s of state.sections) {
    const key = s.competencyId ?? 'none';
    counts[key] = (counts[key] || 0) + s.questions.length;
  }
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const rows = [
    ...competencies
      .map((c) => ({
        id: c._id,
        label: c.title,
        color: competencyColor.get(c._id) || '#868E96',
        n: counts[c._id] || 0,
      }))
      .filter((r) => r.n > 0),
    ...(counts.none
      ? [
          {
            id: 'none',
            label: t('review360.templateEditor.competencyNoLink'),
            color: 'var(--tpl-soft)',
            n: counts.none,
          },
        ]
      : []),
  ];
  return (
    <div className="tpl-summary">
      <div className="tpl-eyebrow">{t('review360.templateEditor.coverageEyebrow')}</div>
      {rows.length === 0 || total === 0 ? (
        <div className="tpl-coverage-empty">{t('review360.templateEditor.coverageEmpty')}</div>
      ) : (
        <div className="tpl-coverage-rows">
          {rows.map((r) => (
            <div key={r.id}>
              <div className="tpl-coverage-row-head">
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                >
                  <span className="tpl-dot" style={{ background: r.color }} />
                  {r.label}
                </span>
                <span className="num" style={{ fontSize: 12, color: 'var(--tpl-muted)' }}>
                  {r.n}
                </span>
              </div>
              <div className="tpl-coverage-bar">
                <div
                  className="tpl-coverage-fill"
                  style={{ width: `${(r.n / total) * 100}%`, background: r.color }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LivePreview: React.FC<{
  state: UiState;
  competencies: Competency[];
  competencyColor: Map<string, string>;
  device: 'desktop' | 'phone';
  onChangeDevice: (d: 'desktop' | 'phone') => void;
  totalQuestions: number;
  estTime: number;
}> = ({
  state,
  competencies,
  competencyColor,
  device,
  onChangeDevice,
  totalQuestions,
  estTime,
}) => {
  const { t } = useTranslation();
  return (
    <div className="tpl-preview">
      <div className="tpl-preview-head">
        <div>
          <div className="tpl-eyebrow">{t('review360.templateEditor.previewEyebrow')}</div>
          <div className="tpl-preview-sub">{t('review360.templateEditor.previewSub')}</div>
        </div>
        <div className="tpl-device-toggle">
          {(
            [
              { v: 'desktop', icon: <IconDeviceDesktop size={14} /> },
              { v: 'phone', icon: <IconDeviceMobile size={14} /> },
            ] as Array<{ v: 'desktop' | 'phone'; icon: React.ReactNode }>
          ).map((d) => {
            const active = d.v === device;
            return (
              <button
                key={d.v}
                type="button"
                onClick={() => onChangeDevice(d.v)}
                className={`tpl-device${active ? ' tpl-device-active' : ''}`}
              >
                {d.icon}
              </button>
            );
          })}
        </div>
      </div>
      <div className="tpl-preview-body">
        <div className={`tpl-preview-frame ${device === 'phone' ? 'tpl-preview-frame-phone' : ''}`}>
          {/* Intro */}
          <div className="tpl-preview-intro">
            <span className="tpl-pill tpl-pill-accent">
              <IconLock size={11} /> {t('review360.templateEditor.previewAnonymousPill')}
            </span>
            <h2 className="tpl-preview-title">
              {state.name || (
                <span style={{ color: 'var(--tpl-soft)' }}>
                  {t('review360.templateEditor.previewTitlePlaceholder')}
                </span>
              )}
            </h2>
            <p className="tpl-preview-desc">
              {state.description || (
                <span style={{ color: 'var(--tpl-soft)' }}>
                  {t('review360.templateEditor.previewDescPlaceholder')}
                </span>
              )}
            </p>
            <div className="tpl-preview-meta">
              <span>
                <span className="num">{state.sections.length}</span>{' '}
                {t('review360.templateEditor.previewSectionsSuffix')}
              </span>
              <span className="tpl-dot-mini" />
              <span>
                <span className="num">{totalQuestions}</span>{' '}
                {t('review360.templateEditor.previewQuestionsSuffix')}
              </span>
              <span className="tpl-dot-mini" />
              <span>
                ≈ <span className="num">{estTime}</span> {t('review360.templateEditor.previewMin')}
              </span>
            </div>
          </div>

          {state.sections.length === 0 ? (
            <div className="tpl-preview-empty">{t('review360.templateEditor.previewEmpty')}</div>
          ) : (
            <div className="tpl-preview-sections">
              {state.sections.map((s, i) => (
                <PreviewSection
                  key={s._id}
                  section={s}
                  idx={i + 1}
                  competencies={competencies}
                  competencyColor={competencyColor}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewSection: React.FC<{
  section: UiSection;
  idx: number;
  competencies: Competency[];
  competencyColor: Map<string, string>;
}> = ({ section, idx, competencies, competencyColor }) => {
  const { t } = useTranslation();
  const comp = competencies.find((c) => c._id === section.competencyId);
  const color = comp ? competencyColor.get(comp._id) : null;
  return (
    <div>
      <div className="tpl-preview-section-head">
        <span className="num tpl-preview-section-num">{String(idx).padStart(2, '0')}</span>
        <div className="tpl-preview-section-divider" />
        {comp && color && (
          <span className="tpl-preview-comp" style={{ background: `${color}1F`, color }}>
            <span className="tpl-dot" style={{ background: color }} />
            {comp.title}
          </span>
        )}
      </div>
      <h3 className="tpl-preview-section-title">
        {section.title || (
          <span style={{ color: 'var(--tpl-soft)' }}>
            {t('review360.templateEditor.previewSectionTitlePlaceholder')}
          </span>
        )}
      </h3>
      <div className="tpl-preview-q-list">
        {section.questions.map((q) => (
          <PreviewQuestion key={q._id} q={q} />
        ))}
      </div>
    </div>
  );
};

const PreviewQuestion: React.FC<{ q: UiQuestion }> = ({ q }) => {
  const { t } = useTranslation();
  const scale = scaleOf(q.scaleId);
  const cells = Array.from({ length: scale.max - scale.min + 1 }, (_, i) => i + scale.min);
  const leftLabel = t(scale.leftKey, scale.leftFallback);
  const rightLabel = t(scale.rightKey, scale.rightFallback);
  return (
    <div>
      <div className="tpl-preview-q-text">
        <p>
          {q.text || (
            <span style={{ color: 'var(--tpl-soft)' }}>
              {t('review360.templateEditor.previewQuestionTextPlaceholder')}
            </span>
          )}
          {q.required && <span className="tpl-required">*</span>}
        </p>
        {q.anonymous && (
          <span className="tpl-pill tpl-pill-ghost">
            <IconLock size={11} />
          </span>
        )}
      </div>
      {q.type === 'rating' ? (
        <div>
          <div className="tpl-preview-scale">
            {cells.map((n) => (
              <div key={n} className="tpl-preview-scale-cell">
                {n}
              </div>
            ))}
            <div className="tpl-preview-scale-na">
              {t('review360.templateEditor.previewScaleNa')}
            </div>
          </div>
          <div className="tpl-preview-scale-labels">
            <span>{leftLabel}</span>
            <span>{rightLabel}</span>
          </div>
        </div>
      ) : (
        <div className="tpl-preview-open">
          {t('review360.templateEditor.previewOpenPlaceholder')}
        </div>
      )}
    </div>
  );
};

function scaleOf(id: ScalePresetId): ScalePreset {
  return SCALE_PRESETS.find((p) => p.id === id) ?? SCALE_PRESETS[2];
}

// ────────────────────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────────────────────
const templateEditorCss = `
  .tpl-page {
    --tpl-bg: var(--mantine-color-body);
    --tpl-surface: var(--mantine-color-body);
    --tpl-surface-2: var(--mantine-color-default-hover);
    --tpl-line: var(--mantine-color-default-border);
    --tpl-ink: var(--mantine-color-text);
    --tpl-muted: var(--mantine-color-dimmed);
    --tpl-soft: var(--mantine-color-placeholder);
    --tpl-accent: var(--mantine-primary-color-filled);
    --tpl-accent-soft: var(--mantine-primary-color-light);
    --tpl-accent-deep: var(--mantine-primary-color-light-color);
    --tpl-warn: #F08C00;
    --tpl-warn-soft: rgba(240,140,0,0.14);
    --tpl-danger: var(--mantine-color-red-6);
    --tpl-danger-soft: rgba(224,49,49,0.12);
    color: var(--tpl-ink);
    padding: 24px 32px 120px;
    max-width: 1400px;
    margin: 0 auto;
  }
  .tpl-page .num { font-variant-numeric: tabular-nums; }
  .tpl-eyebrow { font-size: 11px; color: var(--tpl-muted); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 600; }

  .tpl-bar { display: flex; align-items: center; gap: 8px; padding-bottom: 16px; margin-bottom: 24px; border-bottom: 1px solid var(--tpl-line); }
  .tpl-pill { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px; line-height: 1; }
  .tpl-pill-accent { background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); }
  .tpl-pill-warn { background: var(--tpl-warn-soft); color: var(--tpl-warn); }
  .tpl-pill-ghost { background: transparent; color: var(--tpl-muted); border: 1px solid var(--tpl-line); }

  .tpl-dot { display: inline-block; width: 6px; height: 6px; border-radius: 999px; }
  .tpl-dot-mini { display: inline-block; width: 3px; height: 3px; border-radius: 999px; background: var(--tpl-soft); }
  .tpl-dot-warn { background: var(--tpl-warn); width: 7px; height: 7px; }
  .tpl-dot-soft { background: var(--tpl-soft); }

  /* Presets gallery */
  .tpl-preset-wrap { max-width: 880px; margin: 40px auto 0; }
  .tpl-hero-title { font-size: 30px; font-weight: 700; letter-spacing: -0.01em; margin: 0 0 10px; }
  .tpl-hero-subtitle { font-size: 15px; color: var(--tpl-muted); line-height: 1.55; margin: 0 0 28px; max-width: 600px; }
  .tpl-preset-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
  .tpl-preset-blank, .tpl-preset-card { display: flex; flex-direction: column; align-items: flex-start; padding: 0; height: 200px; background: var(--tpl-surface); border-radius: 12px; cursor: pointer; text-align: left; font: inherit; color: inherit; transition: border-color 160ms, background 160ms, box-shadow 160ms; overflow: hidden; }
  .tpl-preset-blank { padding: 20px; gap: 12px; border: 2px dashed var(--tpl-line); }
  .tpl-preset-blank:hover { border-color: var(--tpl-accent); background: var(--tpl-accent-soft); }
  .tpl-preset-blank-icon { width: 44px; height: 44px; border-radius: 12px; background: var(--tpl-surface-2); display: flex; align-items: center; justify-content: center; color: var(--tpl-accent-deep); }
  .tpl-preset-card { border: 1px solid var(--tpl-line); }
  .tpl-preset-card:hover { border-color: var(--tpl-accent); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
  .tpl-preset-cover { width: 100%; height: 64px; position: relative; }
  .tpl-preset-cover::after { content: ''; position: absolute; inset: 0; opacity: 0.22; background-image: radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px); background-size: 14px 14px; }
  .tpl-preset-body { padding: 16px; flex: 1; display: flex; flex-direction: column; min-width: 0; }
  .tpl-preset-title { font-size: 15px; font-weight: 600; margin-bottom: 4px; }
  .tpl-preset-desc { font-size: 12px; color: var(--tpl-muted); line-height: 1.5; margin-bottom: auto; }
  .tpl-preset-meta { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--tpl-muted); margin-top: 10px; }

  /* Content layout */
  .tpl-content { display: flex; flex-direction: column; gap: 28px; }
  .tpl-grid { display: grid; grid-template-columns: minmax(0, 1fr) 460px; gap: 24px; align-items: flex-start; }
  @media (max-width: 1200px) { .tpl-grid { grid-template-columns: 1fr; } }
  .tpl-col-main { display: flex; flex-direction: column; gap: 20px; min-width: 0; }

  /* Title hero */
  .tpl-title-input { width: 100%; padding: 4px 0; border: none; outline: none; background: transparent; color: var(--tpl-ink); font-size: 30px; font-weight: 700; letter-spacing: -0.015em; line-height: 1.2; resize: none; margin-bottom: 8px; font-family: inherit; }
  .tpl-desc-input { width: 100%; padding: 4px 0; border: none; outline: none; background: transparent; color: var(--tpl-muted); font-size: 15px; line-height: 1.55; resize: vertical; font-family: inherit; }

  /* Sections editor */
  .tpl-sections { display: flex; flex-direction: column; gap: 12px; }
  .tpl-sections-head { display: flex; justify-content: space-between; align-items: flex-end; }
  .tpl-section-title { font-size: 18px; font-weight: 600; margin: 2px 0 0; }
  .tpl-section-list { display: flex; flex-direction: column; gap: 12px; }
  .tpl-add-section { padding: 14px 20px; background: var(--tpl-surface-2); border: 1px dashed var(--tpl-line); border-radius: 12px; color: var(--tpl-accent-deep); font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px; justify-content: center; }
  .tpl-add-section:hover { border-color: var(--tpl-accent); }

  .tpl-empty { padding: 48px; text-align: center; background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 12px; }
  .tpl-empty-icon { width: 56px; height: 56px; border-radius: 14px; background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); display: inline-flex; align-items: center; justify-content: center; margin-bottom: 16px; }
  .tpl-empty-title { font-size: 18px; font-weight: 600; margin-bottom: 6px; }
  .tpl-empty-desc { font-size: 13px; color: var(--tpl-muted); line-height: 1.55; margin-bottom: 18px; max-width: 420px; margin-left: auto; margin-right: auto; }

  /* Section card */
  .tpl-section { background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 12px; overflow: hidden; }
  .tpl-section-head { display: grid; grid-template-columns: 36px 32px 1fr auto auto auto auto; gap: 12px; padding: 14px 16px; align-items: center; background: var(--tpl-surface-2); }
  .tpl-sort { display: flex; flex-direction: column; gap: 0; align-items: center; }
  .tpl-sort-btn { width: 18px; height: 14px; border: none; background: transparent; color: var(--tpl-muted); cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center; }
  .tpl-sort-btn:disabled { color: var(--tpl-soft); cursor: default; }
  .tpl-num { width: 28px; height: 28px; border-radius: 6px; background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .tpl-section-title-input { border: none; outline: none; background: transparent; font-size: 15px; font-weight: 600; color: var(--tpl-ink); min-width: 0; width: 100%; font-family: inherit; }
  .tpl-section-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; border-top: 1px solid var(--tpl-line); }

  .tpl-comp-trigger { display: inline-flex; align-items: center; gap: 6px; height: 26px; padding: 0 10px; background: var(--tpl-surface); color: var(--tpl-muted); border: 1px solid var(--tpl-line); border-radius: 999px; font-size: 12px; font-weight: 500; cursor: pointer; white-space: nowrap; font-family: inherit; }
  .tpl-comp-dropdown { position: absolute; top: calc(100% + 4px); right: 0; z-index: 30; min-width: 260px; background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 8px; box-shadow: 0 10px 24px rgba(0,0,0,0.12); padding: 4px; max-height: 320px; overflow-y: auto; }
  .tpl-comp-item { display: flex; align-items: center; gap: 8px; width: 100%; padding: 7px 10px; background: transparent; color: var(--tpl-ink); border: none; border-radius: 6px; cursor: pointer; font-size: 13px; text-align: left; font-family: inherit; }
  .tpl-comp-item:hover { background: var(--tpl-surface-2); }
  .tpl-comp-item-active { background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); }
  .tpl-comp-empty { padding: 12px; font-size: 12px; color: var(--tpl-muted); }

  .tpl-icon-btn { width: 30px; height: 30px; border: none; background: transparent; color: var(--tpl-muted); cursor: pointer; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
  .tpl-icon-btn:hover { background: var(--tpl-surface); color: var(--tpl-ink); }
  .tpl-icon-btn-danger:hover { background: var(--tpl-danger-soft); color: var(--tpl-danger); }

  /* Question card */
  .tpl-question { background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 8px; }
  .tpl-question-open { border-color: var(--tpl-line); }
  .tpl-question-head { display: grid; grid-template-columns: 28px 1fr auto auto auto; gap: 12px; padding: 12px 14px; align-items: center; }
  .tpl-q-num { width: 24px; height: 24px; border-radius: 999px; background: var(--tpl-surface-2); color: var(--tpl-muted); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; }
  .tpl-q-input { border: none; outline: none; background: transparent; font-size: 14px; font-weight: 500; color: var(--tpl-ink); width: 100%; font-family: inherit; }
  .tpl-q-typetabs { display: inline-flex; padding: 3px; background: var(--tpl-surface-2); border-radius: 6px; }
  .tpl-q-typetab { padding: 5px 10px; font-size: 12px; font-weight: 500; border: none; border-radius: 4px; background: transparent; color: var(--tpl-muted); cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; }
  .tpl-q-typetab-active { background: var(--tpl-surface); color: var(--tpl-ink); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
  .tpl-question-body { padding: 14px 14px 14px 54px; border-top: 1px solid var(--tpl-line); }
  .tpl-scales { display: flex; flex-wrap: wrap; gap: 6px; }
  .tpl-scale-chip { padding: 6px 12px; font-size: 12px; font-weight: 500; background: var(--tpl-surface); color: var(--tpl-muted); border: 1px solid var(--tpl-line); border-radius: 999px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; }
  .tpl-scale-chip-active { background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); border-color: var(--tpl-accent); }
  .tpl-scale-hint { margin-top: 10px; padding: 10px 12px; background: var(--tpl-surface-2); border-radius: 8px; font-size: 11px; color: var(--tpl-muted); }
  .tpl-toggles { display: flex; gap: 14px; margin-top: 12px; flex-wrap: wrap; }
  .tpl-toggle-row { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--tpl-ink); }
  .tpl-toggle { width: 32px; height: 18px; position: relative; background: var(--tpl-surface-2); border: 1px solid var(--tpl-line); border-radius: 999px; cursor: pointer; padding: 0; }
  .tpl-toggle-on { background: var(--tpl-accent); border-color: var(--tpl-accent); }
  .tpl-toggle-knob { position: absolute; top: 1px; left: 1px; width: 14px; height: 14px; border-radius: 999px; background: #fff; transition: left 160ms cubic-bezier(.2,.8,.2,1); }
  .tpl-toggle-on .tpl-toggle-knob { left: 15px; }

  .tpl-add-question-row { display: flex; gap: 8px; margin-top: 4px; }
  .tpl-add-question { flex: 1; padding: 10px 12px; border: 1px dashed var(--tpl-line); background: transparent; color: var(--tpl-accent-deep); border-radius: 8px; font-size: 12px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-family: inherit; }
  .tpl-add-question:hover { border-color: var(--tpl-accent); }

  /* Summary + coverage */
  .tpl-summary-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  @media (max-width: 900px) { .tpl-summary-row { grid-template-columns: 1fr; } }
  .tpl-summary { padding: 20px; background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 12px; }
  .tpl-summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 12px; }
  .tpl-stat-value { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; margin-top: 4px; }
  .tpl-summary-time { margin-top: 14px; padding: 12px 14px; background: var(--tpl-surface-2); border-radius: 8px; font-size: 12px; color: var(--tpl-ink); display: flex; justify-content: space-between; align-items: center; }
  .tpl-coverage-empty { margin-top: 10px; font-size: 12px; color: var(--tpl-muted); line-height: 1.55; }
  .tpl-coverage-rows { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
  .tpl-coverage-row-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
  .tpl-coverage-bar { height: 4px; background: var(--tpl-surface-2); border-radius: 999px; overflow: hidden; }
  .tpl-coverage-fill { height: 100%; border-radius: 999px; transition: width 200ms ease; }

  /* Preview */
  .tpl-preview { background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 12px; overflow: hidden; position: sticky; top: 16px; }
  .tpl-preview-head { padding: 14px 18px; background: var(--tpl-surface-2); border-bottom: 1px solid var(--tpl-line); display: flex; align-items: center; justify-content: space-between; gap: 12px; }
  .tpl-preview-sub { font-size: 13px; font-weight: 500; margin-top: 2px; }
  .tpl-device-toggle { display: inline-flex; padding: 3px; background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 6px; }
  .tpl-device { width: 28px; height: 24px; border: none; border-radius: 4px; background: transparent; color: var(--tpl-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
  .tpl-device-active { background: var(--tpl-accent-soft); color: var(--tpl-accent-deep); }
  .tpl-preview-body { background: var(--tpl-surface-2); padding: 16px; max-height: 720px; overflow-y: auto; }
  .tpl-preview-frame { background: var(--tpl-surface); border: 1px solid var(--tpl-line); border-radius: 12px; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
  .tpl-preview-frame-phone { max-width: 320px; margin: 0 auto; border-radius: 20px; padding: 16px; }
  .tpl-preview-intro { padding-bottom: 14px; border-bottom: 1px solid var(--tpl-line); }
  .tpl-preview-title { font-size: 20px; font-weight: 700; letter-spacing: -0.01em; margin: 12px 0 6px; line-height: 1.25; }
  .tpl-preview-desc { font-size: 13px; color: var(--tpl-muted); line-height: 1.55; margin: 0 0 12px; }
  .tpl-preview-meta { display: flex; align-items: center; gap: 12px; font-size: 11px; color: var(--tpl-muted); }
  .tpl-preview-empty { margin-top: 24px; padding: 40px 20px; background: var(--tpl-surface-2); border: 1px dashed var(--tpl-line); border-radius: 8px; text-align: center; color: var(--tpl-muted); font-size: 13px; }
  .tpl-preview-sections { margin-top: 20px; display: flex; flex-direction: column; gap: 24px; }
  .tpl-preview-section-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .tpl-preview-section-num { font-size: 10px; color: var(--tpl-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.14em; }
  .tpl-preview-section-divider { flex: 1; height: 1px; background: var(--tpl-line); }
  .tpl-preview-comp { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; }
  .tpl-preview-section-title { font-size: 15px; font-weight: 600; margin: 0 0 12px; letter-spacing: -0.005em; }
  .tpl-preview-q-list { display: flex; flex-direction: column; gap: 14px; }
  .tpl-preview-q-text { display: flex; align-items: flex-start; gap: 6px; margin-bottom: 8px; }
  .tpl-preview-q-text p { flex: 1; font-size: 13px; color: var(--tpl-ink); line-height: 1.5; font-weight: 500; margin: 0; }
  .tpl-required { color: var(--tpl-danger); margin-left: 4px; }
  .tpl-preview-scale { display: flex; align-items: center; gap: 6px; }
  .tpl-preview-scale-cell { flex: 1; height: 38px; border-radius: 6px; border: 1px solid var(--tpl-line); background: var(--tpl-bg); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; color: var(--tpl-muted); font-variant-numeric: tabular-nums; }
  .tpl-preview-scale-na { flex: 1.4; height: 38px; border-radius: 6px; border: 1px dashed var(--tpl-line); background: var(--tpl-bg); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 500; color: var(--tpl-muted); text-align: center; padding: 0 6px; }
  .tpl-preview-scale-labels { display: flex; justify-content: space-between; margin-top: 6px; font-size: 10px; color: var(--tpl-muted); }
  .tpl-preview-open { padding: 12px; min-height: 80px; border: 1px solid var(--tpl-line); border-radius: 8px; background: var(--tpl-bg); color: var(--tpl-soft); font-size: 12px; line-height: 1.5; }

  /* SaveBar */
  .tpl-savebar { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 50; display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #1F2329; color: #F8F9FA; border-radius: 12px; box-shadow: 0 20px 40px -16px rgba(0,0,0,0.35), 0 6px 16px rgba(0,0,0,0.2); min-width: 460px; }
  .tpl-savebar button { height: 34px; padding: 0 12px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
  .tpl-savebar .tpl-discard { border: 1px solid rgba(255,255,255,0.18); background: transparent; color: inherit; }
  .tpl-savebar .tpl-save { border: none; background: var(--tpl-accent); color: #fff; padding: 0 14px; }
  .tpl-savebar .tpl-save:disabled { opacity: 0.5; cursor: not-allowed; }
`;
